import localDb from '@/common/localDb.ts';
import { useBalanceRefresh } from '@/composables/useBalanceRefresh.ts';
import { EVENTS, useEvents } from '@/composables/useEvents.ts';
import { PLAY_ACCOUNT_STARTING_BALANCE, PLAY_ACCOUNT_TOP_UP_COOLDOWN_MS } from '@/common/config.ts';

export const KV_KEY_BALANCE = 'play_balance';
export const KV_KEY_LAST_TOP_UP_AT = 'play_balance_last_top_up_at';

const balanceRefresh = useBalanceRefresh();
const events = useEvents();

// Export balance refresh trigger for external use
export { balanceRefresh };

export async function getBalance(): Promise<bigint> {
  const db = await localDb.openDb();
  const tx = db.transaction(localDb.STORE_PLAY_KV, 'readonly');
  const store = tx.objectStore(localDb.STORE_PLAY_KV);

  return new Promise<bigint>((resolve, reject) => {
    const request = store.get(KV_KEY_BALANCE);

    request.onsuccess = () => {
      resolve(BigInt(request.result || '0'));
    };

    request.onerror = () => {
      reject(request.error);
    };

    tx.oncomplete = () => {
      db.close();
    };
  });
}

export async function setBalance(newBalance: bigint) {
  const db = await localDb.openDb();
  const tx = db.transaction(localDb.STORE_PLAY_KV, 'readwrite');
  const store = tx.objectStore(localDb.STORE_PLAY_KV);

  store.put(newBalance.toString(), KV_KEY_BALANCE);

  await new Promise((res, rej) => {
    tx.oncomplete = res;
    tx.onerror = rej;
  });

  db.close();

  balanceRefresh.triggerRefresh();
}

export async function initBalance() {
  const balance = await getBalance();
  if (balance === BigInt(0)) {
    await setBalance(PLAY_ACCOUNT_STARTING_BALANCE);
  }
}

export async function increaseBalance(amount: bigint) {
  const balance = await getBalance();
  const newBalance = balance + amount;
  await setBalance(newBalance);
}

export async function decreaseBalance(amount: bigint) {
  const balance = await getBalance();
  const newBalance = balance - amount;
  await setBalance(newBalance);
}

export async function topUpBalance() {
  const balance = await getBalance();

  if (balance < PLAY_ACCOUNT_STARTING_BALANCE) {
    const db = await localDb.openDb();
    const tx = db.transaction(localDb.STORE_PLAY_KV, 'readwrite');
    const store = tx.objectStore(localDb.STORE_PLAY_KV);

    const lastTopUpAtRequest = store.get(KV_KEY_LAST_TOP_UP_AT);

    lastTopUpAtRequest.onsuccess = async () => {
      const lastTopUpAt = lastTopUpAtRequest.result ? new Date(lastTopUpAtRequest.result) : null;
      const now = new Date();

      if (!lastTopUpAt || now.getTime() - lastTopUpAt.getTime() > PLAY_ACCOUNT_TOP_UP_COOLDOWN_MS) {
        store.put(now.toISOString(), KV_KEY_LAST_TOP_UP_AT);
        await setBalance(PLAY_ACCOUNT_STARTING_BALANCE);

        events.emit(EVENTS.PLAY_BALANCE_TOP_UP, { amount: PLAY_ACCOUNT_STARTING_BALANCE });
      }
    };

    await new Promise((res, rej) => {
      tx.oncomplete = res;
      tx.onerror = rej;
    });

    db.close();
  }
}

export default {
  getBalance,
  setBalance,
  initBalance,
  increaseBalance,
  decreaseBalance,
};
